import type { StageDef } from './data/maps';
import type { IPhysics } from './IPhysics';
import type { MapEntity, MapEntityState } from './types/MapEntity.type';

interface Snapshot {
  t: number;
  x: number;
  y: number;
  angle: number;
}

/**
 * 원격 수신 프레임으로 마블 위치를 제공하는 가짜 물리 엔진.
 * 부드러운 재생을 위해 최근 프레임 버퍼(5개)에 대해 선형 보간 적용.
 * render delay = 100ms (네트워크 지터 흡수 + 항상 두 프레임 사이 보간 가능).
 */
const RENDER_DELAY_MS = 100;
const BUFFER_LIMIT = 6;

export class RemotePhysics implements IPhysics {
  private buffers = new Map<number, Snapshot[]>();
  private entities: MapEntityState[] = [];

  async init(): Promise<void> {}
  clear(): void {
    this.buffers.clear();
    this.entities = [];
  }
  clearMarbles(): void {
    this.buffers.clear();
  }

  createStage(stage: StageDef): void {
    // 맵 엔티티는 render 시 참조되므로 형태만 저장
    this.entities = (stage.entities ?? []).map((e) => ({
      x: e.position.x,
      y: e.position.y,
      angle: 0,
      shape: e.shape,
      life: e.props.life ?? -1,
    }));
  }
  createEntities(_entities?: MapEntity[]): void {}

  createMarble(id: number, x: number, y: number): void {
    const now = performance.now();
    this.buffers.set(id, [{ t: now, x, y, angle: 0 }]);
  }
  shakeMarble(_id: number): void {}
  removeMarble(id: number): void {
    this.buffers.delete(id);
  }

  getMarblePosition(id: number): { x: number; y: number; angle: number } {
    const buf = this.buffers.get(id);
    if (!buf || buf.length === 0) return { x: 0, y: 0, angle: 0 };
    if (buf.length === 1) return { x: buf[0].x, y: buf[0].y, angle: buf[0].angle };

    const renderTime = performance.now() - RENDER_DELAY_MS;

    // renderTime 이 속한 두 스냅샷을 찾아 선형 보간
    for (let i = buf.length - 1; i > 0; i--) {
      const a = buf[i - 1];
      const b = buf[i];
      if (a.t <= renderTime && renderTime <= b.t) {
        const span = b.t - a.t;
        const ratio = span > 0 ? (renderTime - a.t) / span : 1;
        return {
          x: a.x + (b.x - a.x) * ratio,
          y: a.y + (b.y - a.y) * ratio,
          angle: a.angle + (b.angle - a.angle) * ratio,
        };
      }
    }

    // renderTime 이 최신 스냅샷보다 뒤 → 외삽 대신 최신 값 사용
    const last = buf[buf.length - 1];
    return { x: last.x, y: last.y, angle: last.angle };
  }

  getEntities(): MapEntityState[] {
    return this.entities;
  }
  impact(_id: number): void {}
  start(): void {}
  step(_deltaSeconds: number): void {}

  // ─── 외부 주입 API ───
  applyFrame(frame: Array<{ id: number; x: number; y: number; a: number }>): void {
    const now = performance.now();
    for (const f of frame) {
      let buf = this.buffers.get(f.id);
      if (!buf) {
        buf = [];
        this.buffers.set(f.id, buf);
      }
      buf.push({ t: now, x: f.x, y: f.y, angle: f.a });
      while (buf.length > BUFFER_LIMIT) buf.shift();
    }
  }
}
